/**
 * ExportJobPage — /export/:jobId, one server-side compare export: progress
 * while it builds, then a preview and the download.
 *
 * Progress is a single percentage from the server (export_jobs.progress_pct);
 * the stage label is derived from it (utils/exportProgress). A running job
 * whose heartbeat is older than 2 minutes is shown as "not responding" — the
 * worker has likely restarted and another claim will pick it up.
 *
 * The download link is minted fresh on every click, so it never goes stale
 * while the file exists (7 days). See docs/export/SERVER_SIDE_EXPORT_PLAN.md §6.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ACTIVE_EXPORT_STATUSES, exportJobsApi, type ExportJob } from '../services/exportJobsApi';
import { EXPORT_STAGE_LABEL, stageForPercent } from '../utils/exportProgress';

const POLL_MS = 2000;
const STALE_HEARTBEAT_MS = 2 * 60 * 1000;

const FORMAT_LABEL: Record<string, string> = { xlsx: 'Excel', docx: 'Word', html: 'HTML' };

function formatBytes(n: number | null): string {
  if (!n) return '';
  return n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

function statusText(job: ExportJob, now: number): string {
  if (job.status === 'awaiting_upload') return 'Sending the labels to the server…';
  if (job.status === 'pending') return 'Waiting in the export queue…';
  if (job.status === 'running') {
    if (job.heartbeat_at && now - new Date(job.heartbeat_at).getTime() > STALE_HEARTBEAT_MS) {
      return "The export server isn't responding — retrying…";
    }
    const stage = stageForPercent(job.progress_pct);
    return stage === 'build' ? `${EXPORT_STAGE_LABEL.build}…` : `${EXPORT_STAGE_LABEL[stage]}… ${job.progress_pct}%`;
  }
  return '';
}

export default function ExportJobPage() {
  const { jobId = '' } = useParams();
  const [job, setJob] = useState<ExportJob | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      setJob(await exportJobsApi.get(jobId));
      setLoadError(null);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setLoadError(status === 404 ? 'This export was not found.' : 'Could not load this export. Retrying…');
    }
    setNow(Date.now());
  }, [jobId]);

  const active = !!job && ACTIVE_EXPORT_STATUSES.includes(job.status);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (job && !active && !loadError) return;
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [active, job, load, loadError]);

  useEffect(() => {
    if (job?.status !== 'complete' || previewUrl) return;
    exportJobsApi.previewUrl(jobId).then((r) => setPreviewUrl(r.url)).catch(() => {});
  }, [job?.status, jobId, previewUrl]);

  const onDownload = async () => {
    setActionError(null);
    setDownloading(true);
    try {
      const { url } = await exportJobsApi.downloadUrl(jobId);
      window.location.href = url; // attachment disposition: the page stays
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setActionError(status === 410 ? 'This export expired. Run it again from the comparison.' : 'The download could not be started. Please try again.');
      if (status === 410) void load();
    } finally {
      setDownloading(false);
    }
  };

  const onCancel = async () => {
    try {
      setJob(await exportJobsApi.cancel(jobId));
    } catch {
      setActionError('Could not cancel the export.');
    }
  };

  const returnUrl = job?.request?.return_url;
  const title = job ? `${FORMAT_LABEL[job.format] ?? job.format} export — “${job.request?.keyword ?? ''}”` : 'Export';
  const drugCount = job?.request?.drug_ids?.length ?? 0;
  // Over the page-image cap the server builds the export in parts, zipped.
  const isZip = !!job?.result_filename?.toLowerCase().endsWith('.zip');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-800">{title}</h1>
            {job && (
              <p className="text-xs text-slate-500">
                {drugCount} drug(s){job.request?.section ? ` · section ${job.request.section}` : ''} · started{' '}
                {new Date(job.created_at).toLocaleString()}
              </p>
            )}
          </div>
          {returnUrl && (
            <Link to={returnUrl} className="text-sm font-medium text-indigo-600 hover:underline">
              ← Back to the comparison
            </Link>
          )}
        </div>

        {loadError && !job && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">{loadError}</div>}

        {job && active && (
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-2 flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>{statusText(job, now)}</span>
              <button onClick={onCancel} className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:bg-slate-50">
                Cancel
              </button>
            </div>
            <div className="h-2 overflow-hidden rounded bg-slate-100">
              <div
                className={`h-full bg-indigo-500 transition-[width] duration-500 ${stageForPercent(job.progress_pct) === 'build' ? 'animate-pulse' : ''}`}
                style={{ width: `${Math.max(2, job.progress_pct)}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-slate-400">
              You can close this page — the export keeps running, and this link stays valid for 7 days.
            </p>
          </div>
        )}

        {job && job.status === 'complete' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-sm text-slate-700">
                <div className="font-medium">{job.result_filename}</div>
                <div className="text-xs text-slate-500">
                  {formatBytes(job.result_bytes)}
                  {job.image_note ? ` · ${job.image_note}` : ''} · available until {new Date(job.expires_at).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={onDownload}
                disabled={downloading}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {downloading ? 'Starting…' : isZip ? `Download ZIP (${FORMAT_LABEL[job.format] ?? ''} files)` : `Download ${FORMAT_LABEL[job.format] ?? ''}`}
              </button>
            </div>
            {isZip ? (
              <p className="text-xs text-slate-500">
                This export has too many PDF pages for one file, so it comes as a ZIP of {FORMAT_LABEL[job.format]} files —
                each one a complete comparison (text and page images) for its drugs. The preview below shows all drugs together.
              </p>
            ) : job.format !== 'html' && (
              <p className="text-xs text-slate-500">
                A web preview of the same content — download the file for the exact {FORMAT_LABEL[job.format]} formatting.
              </p>
            )}
            {previewUrl ? (
              <iframe title="Export preview" src={previewUrl} className="h-[75vh] w-full rounded-lg border border-slate-200 bg-white" />
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">Loading the preview…</div>
            )}
          </div>
        )}

        {job && (job.status === 'error' || job.status === 'cancelled' || job.status === 'expired') && (
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
            <p className={job.status === 'error' ? 'text-red-700' : 'text-slate-700'}>
              {job.status === 'expired'
                ? 'This export expired and its file was deleted.'
                : job.status === 'cancelled'
                  ? 'This export was cancelled.'
                  : job.error_message ?? 'The export failed.'}
            </p>
            {returnUrl && (
              <Link to={returnUrl} className="mt-2 inline-block font-medium text-indigo-600 hover:underline">
                Run it again from the comparison →
              </Link>
            )}
          </div>
        )}

        {actionError && <p className="mt-3 text-sm text-red-700">{actionError}</p>}
      </div>
    </div>
  );
}
